import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Table } from '@heroui/react';
import { Sheet } from '@heroui-pro/react';
import type { LocaleCode } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import { formatInLanguage } from '@/utils/dateLocale';
import { formatGel } from '@/utils/money';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    NO_FIGURE,
    excludedCount,
    formatTetriOrDash,
    isEmptyRollup,
    rollupBalances,
} from '../../shiftsMeta';
import { useShiftRollupDrawer } from './useShiftRollupDrawer';

type ShiftRollupDrawerProps = {
    /** The shift to summarize; `null` closes the drawer. */
    shiftId: string | null;
    locale: LocaleCode;
    onClose: () => void;
};

const TIMESTAMP_FORMAT = 'DD/MM/YYYY HH:mm';

const Field: React.FC<{ label: string; value: string; slot: string }> = ({
    label,
    value,
    slot,
}) => (
    <div className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-muted">
            {label}
        </span>
        <span
            className="text-sm font-semibold tabular-nums text-foreground"
            {...getNodeTestIdProp(`branches-shifts-rollup-${slot}`)}
        >
            {value}
        </span>
    </div>
);

/**
 * One shift's combined ledger balance.
 *
 * The figures a consolidated shift entry would have carried, read back out of
 * the per-receipt entries the till actually posted. Sales still post one entry
 * each — that is what the accountant confirmed, and it is what keeps every
 * amount traceable to its own receipt; this drawer gives back the readability
 * consolidation would have provided, without the system having consolidated
 * anything.
 *
 * **The same visual grammar as a journal entry**: account code and name, a debit
 * and a credit column, a totals row that proves the two sides agree. A rollup
 * that summarizes entries ought to read like the entries it summarizes.
 *
 * **What is left out is stated, not implied.** The line under the table says how
 * many receipts are in the totals, how many were reversed, and how many posted
 * nothing — including when those counts are zero, because "nothing was excluded"
 * is information and its absence reads as an oversight.
 */
export const ShiftRollupDrawer: React.FC<ShiftRollupDrawerProps> = ({
    shiftId,
    locale,
    onClose,
}) => {
    const { t, i18n } = useTranslation('pages');
    const drawer = useShiftRollupDrawer({ shiftId, locale });
    const { summary } = drawer;

    return (
        <Sheet
            isOpen={shiftId !== null}
            onOpenChange={(open) => !open && onClose()}
            placement="right"
        >
            <Sheet.Backdrop>
                <Sheet.Content className="w-full sm:max-w-[680px]">
                    <Sheet.Dialog
                        aria-label={t('branches.shifts.rollup.title')}
                        {...getNodeTestIdProp('branches-shifts-rollup-drawer')}
                    >
                        <Sheet.CloseTrigger />
                        <Sheet.Header>
                            <Sheet.Heading>
                                <span className="flex flex-col gap-1">
                                    <span
                                        className="text-lg font-semibold"
                                        {...getNodeTestIdProp(
                                            'branches-shifts-rollup-heading',
                                        )}
                                    >
                                        {summary
                                            ? formatInLanguage(
                                                  summary.shift.openedAt,
                                                  TIMESTAMP_FORMAT,
                                                  i18n.language,
                                              )
                                            : t('branches.shifts.rollup.title')}
                                    </span>
                                    <span className="text-sm font-normal text-muted">
                                        {summary
                                            ? t(
                                                  'branches.shifts.rollup.subtitle',
                                                  {
                                                      branch: summary.shift
                                                          .branchName,
                                                  },
                                              )
                                            : t('branches.shifts.rollup.title')}
                                    </span>
                                </span>
                            </Sheet.Heading>
                        </Sheet.Header>

                        <Sheet.Body className="flex flex-col gap-5">
                            {drawer.hasError ? (
                                <p
                                    className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                                    {...getNodeTestIdProp(
                                        'branches-shifts-rollup-error',
                                    )}
                                >
                                    {t('branches.shifts.rollup.error')}
                                </p>
                            ) : drawer.isLoading || !summary ? (
                                <p
                                    className="text-sm text-muted"
                                    {...getNodeTestIdProp(
                                        'branches-shifts-rollup-loading',
                                    )}
                                >
                                    {t('branches.shifts.rollup.loading')}
                                </p>
                            ) : (
                                <>
                                    {/* The drawer's own cash audit — context for
                                        the ledger below, and the one thing a
                                        quiet shift still has to say. */}
                                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                                        <Field
                                            label={t(
                                                'branches.shifts.rollup.openingFloat',
                                            )}
                                            slot="opening-float"
                                            value={formatGel(
                                                summary.shift.openingFloatTetri,
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'branches.shifts.rollup.expected',
                                            )}
                                            slot="expected"
                                            value={formatTetriOrDash(
                                                summary.shift
                                                    .expectedAtCloseTetri,
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'branches.shifts.rollup.counted',
                                            )}
                                            slot="counted"
                                            value={formatTetriOrDash(
                                                summary.shift.closingCountTetri,
                                            )}
                                        />
                                        <Field
                                            label={t(
                                                'branches.shifts.rollup.discrepancy',
                                            )}
                                            slot="discrepancy"
                                            value={formatTetriOrDash(
                                                summary.shift
                                                    .closeDiscrepancyTetri,
                                            )}
                                        />
                                    </div>

                                    {isEmptyRollup(summary) ? (
                                        <div
                                            className="flex flex-col items-center gap-3 rounded-xl bg-surface-secondary py-10 text-muted"
                                            {...getNodeTestIdProp(
                                                'branches-shifts-rollup-empty',
                                            )}
                                        >
                                            <Icon
                                                aria-hidden
                                                className="size-8 text-default-500"
                                                icon="gravity-ui:folder-open"
                                            />
                                            <span className="max-w-sm text-center text-sm">
                                                {t(
                                                    'branches.shifts.rollup.empty',
                                                )}
                                            </span>
                                        </div>
                                    ) : (
                                        <div
                                            {...getNodeTestIdProp(
                                                'branches-shifts-rollup-table',
                                            )}
                                        >
                                            <Table>
                                                <Table.ScrollContainer>
                                                    <Table.Content
                                                        aria-label={t(
                                                            'branches.shifts.rollup.tableAria',
                                                        )}
                                                        className="min-w-[22rem] text-sm"
                                                    >
                                                        <Table.Header>
                                                            <Table.Column
                                                                id="account"
                                                                isRowHeader
                                                            >
                                                                {t(
                                                                    'branches.shifts.rollup.account',
                                                                )}
                                                            </Table.Column>
                                                            <Table.Column id="debit">
                                                                {t(
                                                                    'branches.shifts.rollup.debit',
                                                                )}
                                                            </Table.Column>
                                                            <Table.Column id="credit">
                                                                {t(
                                                                    'branches.shifts.rollup.credit',
                                                                )}
                                                            </Table.Column>
                                                        </Table.Header>
                                                        <Table.Body>
                                                            {summary.accounts.map(
                                                                (account) => (
                                                                    <Table.Row
                                                                        id={
                                                                            account.accountId
                                                                        }
                                                                        key={
                                                                            account.accountId
                                                                        }
                                                                    >
                                                                        <Table.Cell>
                                                                            <span className="flex min-w-0 items-center gap-2">
                                                                                <span className="shrink-0 text-xs tabular-nums text-muted">
                                                                                    {
                                                                                        account.code
                                                                                    }
                                                                                </span>
                                                                                <span className="truncate text-foreground">
                                                                                    {
                                                                                        account.name
                                                                                    }
                                                                                </span>
                                                                            </span>
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            <span className="block whitespace-nowrap text-right tabular-nums text-foreground">
                                                                                {account.debitTetri >
                                                                                0
                                                                                    ? formatGel(
                                                                                          account.debitTetri,
                                                                                      )
                                                                                    : NO_FIGURE}
                                                                            </span>
                                                                        </Table.Cell>
                                                                        <Table.Cell>
                                                                            <span className="block whitespace-nowrap text-right tabular-nums text-foreground">
                                                                                {account.creditTetri >
                                                                                0
                                                                                    ? formatGel(
                                                                                          account.creditTetri,
                                                                                      )
                                                                                    : NO_FIGURE}
                                                                            </span>
                                                                        </Table.Cell>
                                                                    </Table.Row>
                                                                ),
                                                            )}
                                                            <Table.Row
                                                                className="bg-surface-secondary font-semibold"
                                                                id="branches-shifts-rollup-totals"
                                                                key="branches-shifts-rollup-totals"
                                                            >
                                                                <Table.Cell>
                                                                    {t(
                                                                        'branches.shifts.rollup.totals',
                                                                    )}
                                                                </Table.Cell>
                                                                <Table.Cell
                                                                    {...getNodeTestIdProp(
                                                                        'branches-shifts-rollup-total-debit',
                                                                    )}
                                                                >
                                                                    <span className="block whitespace-nowrap text-right tabular-nums">
                                                                        {formatGel(
                                                                            summary.totalDebitTetri,
                                                                        )}
                                                                    </span>
                                                                </Table.Cell>
                                                                <Table.Cell
                                                                    {...getNodeTestIdProp(
                                                                        'branches-shifts-rollup-total-credit',
                                                                    )}
                                                                >
                                                                    <span className="block whitespace-nowrap text-right tabular-nums">
                                                                        {formatGel(
                                                                            summary.totalCreditTetri,
                                                                        )}
                                                                    </span>
                                                                </Table.Cell>
                                                            </Table.Row>
                                                        </Table.Body>
                                                    </Table.Content>
                                                </Table.ScrollContainer>
                                            </Table>
                                        </div>
                                    )}

                                    {/* Both sides came from balanced entries, so
                                        they agree — which is exactly why it is
                                        worth saying out loud rather than
                                        assuming. */}
                                    {!rollupBalances(summary) ? (
                                        <p
                                            className="rounded-xl bg-danger-soft px-4 py-3 text-sm text-danger-soft-foreground"
                                            {...getNodeTestIdProp(
                                                'branches-shifts-rollup-imbalance',
                                            )}
                                        >
                                            {t(
                                                'branches.shifts.rollup.imbalance',
                                            )}
                                        </p>
                                    ) : null}

                                    <p
                                        className={cn(
                                            'text-xs',
                                            excludedCount(summary) > 0
                                                ? 'text-warning'
                                                : 'text-muted',
                                        )}
                                        {...getNodeTestIdProp(
                                            'branches-shifts-rollup-coverage',
                                        )}
                                    >
                                        {t('branches.shifts.rollup.coverage', {
                                            included: summary.receiptsIncluded,
                                            reversed: summary.receiptsReversed,
                                            unposted: summary.receiptsUnposted,
                                        })}
                                    </p>
                                </>
                            )}
                        </Sheet.Body>
                    </Sheet.Dialog>
                </Sheet.Content>
            </Sheet.Backdrop>
        </Sheet>
    );
};
